import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';

// A single filter control, optionally with an explicit size. `width` is the
// desired column width — a number (px) or a CSS length ("18rem", "20%"). The
// field is sized to the content it holds (it never grows to fill the row), and
// the bar fits as many as the available width allows.
export interface FilterField {
  node: ReactNode;
  width?: number | string;
  // Keep this field on the always-visible row no matter how little space there
  // is — used for date-range / date-picker fields, which stay inline while the
  // other filters collapse behind the "All Filters" button.
  pinned?: boolean;
}

type Field = ReactNode | FilterField;

interface FilterBarProps {
  // Each entry is a rendered control (label + input). Pass a bare node for the
  // default width, or `{ node, width }` to size it to its content. Mark date
  // fields `{ node, pinned: true }` so they stay visible when space is tight.
  fields: Field[];
  // Switches / mode buttons — ALWAYS visible (never collapsed).
  modes?: ReactNode;
  // Action buttons (Fetch / Clear / Extract …) — ALWAYS visible.
  actions?: ReactNode;
  // Connection/status slot, pinned to the far right.
  status?: ReactNode;
  // Count badge on the "All Filters" button (e.g. number of active hidden filters).
  activeCount?: number;
  allFiltersLabel?: string;
  // Default min-width (px) for fields that don't specify their own `width`.
  defaultFieldWidth?: number;
  // Wrap in the standard filter card. Off lets a page embed the bar in its own card.
  card?: boolean;
  className?: string;

  // --- deprecated (accepted for back-compat, no longer used) --------------------
  // The inline field count is now purely space-based — the bar fits as many as the
  // width allows and collapses the rest. A fixed cap is ignored.
  visibleCount?: number;
  // Replaced by per-field `width`. Layout is now flex + per-field sizing.
  colClassName?: string;
  // Actions/switches are always inline now, so this is a no-op.
  inlineActions?: boolean;
}

// gap-2 == 0.5rem == 8px between flex items; a little slack keeps the last inline
// field from butting against the always-visible rail and wrapping on a sub-pixel.
const GAP = 8;
const SAFETY = 8;

function asField(f: Field): FilterField {
  // A FilterField is a plain object carrying `node`; a ReactElement/array/etc. is not.
  if (f != null && typeof f === 'object' && 'node' in (f as object)) {
    return f as FilterField;
  }
  return { node: f as ReactNode };
}

// Best-effort px width for the fit math. Numbers and px/rem lengths are exact;
// anything else (%, calc()) falls back to the default so it still participates.
function widthPx(f: FilterField, fallback: number): number {
  const w = f.width;
  if (w == null) return fallback;
  if (typeof w === 'number') return w;
  const m = /^([\d.]+)(px|rem)?$/.exec(w.trim());
  if (m) return m[2] === 'rem' ? parseFloat(m[1]) * 16 : parseFloat(m[1]);
  return fallback;
}

function fieldStyle(width: number | string | undefined, fallback: number): CSSProperties {
  const w = width ?? fallback;
  const len = typeof w === 'number' ? `${w}px` : w;
  // Hard-fix each field to exactly its declared width (no grow, no shrink, and
  // minWidth:0 so content can't force it wider). The painted width then equals the
  // width the fit math assumes, so the bar packs exactly as computed and a field can
  // never render wider than budgeted and shove the always-visible rail off its line.
  return { flex: `0 0 ${len}`, width: len, minWidth: 0, maxWidth: len };
}

// Shared filter bar (Risk-Dashboard style): the switches + action buttons stay on
// an always-visible right rail, and as many filter fields as the current width
// allows sit on the left. The overflow collapses behind an "All Filters" button;
// pinned (date) fields never collapse. The inline count is recomputed on resize /
// zoom, so narrowing the viewport hides filters rather than wrapping the rail.
export function FilterBar({
  fields,
  modes,
  actions,
  status,
  activeCount,
  allFiltersLabel = 'All Filters',
  defaultFieldWidth = 180,
  card = true,
  className,
}: FilterBarProps) {
  const [showAll, setShowAll] = useState(false);
  const norm = useMemo(() => fields.map(asField), [fields]);

  const rowRef = useRef<HTMLDivElement>(null);
  const railRef = useRef<HTMLDivElement>(null);
  // Horizontal space left for the inline fields (row width minus the always-visible
  // rail). null until first measured; the layout effect fills it before paint.
  const [budget, setBudget] = useState<number | null>(null);

  useLayoutEffect(() => {
    const row = rowRef.current;
    if (!row) return;
    const measure = () => {
      const total = row.clientWidth; // flex row fills its parent → stable vs. children
      const rail = railRef.current?.offsetWidth ?? 0;
      const next = Math.max(0, Math.floor(total - rail - GAP - SAFETY));
      // Ignore sub-pixel churn so toggling the rail's "All Filters" button can't loop.
      setBudget((prev) => (prev != null && Math.abs(prev - next) < 2 ? prev : next));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(row);
    if (railRef.current) ro.observe(railRef.current);
    return () => ro.disconnect();
  }, [norm, modes, actions, status]);

  const { primary, overflow } = useMemo(() => {
    const withIdx = norm.map((f, idx) => ({ f, idx }));
    // Before the first measurement, show everything; the layout effect corrects
    // this synchronously (before paint), so there's no flash of all fields.
    if (budget == null) return { primary: withIdx, overflow: [] as typeof withIdx };

    let remaining = budget;
    // Pinned fields (date range / picker) are always inline — charge them first.
    for (const f of norm) if (f.pinned) remaining -= widthPx(f, defaultFieldWidth) + GAP;

    const visible = new Set<FilterField>();
    let full = false; // once one field doesn't fit, keep the rest collapsed (preserve order)
    for (const f of norm) {
      if (f.pinned) continue;
      const w = widthPx(f, defaultFieldWidth) + GAP;
      if (!full && w <= remaining) {
        visible.add(f);
        remaining -= w;
      } else {
        full = true;
      }
    }

    return {
      primary: withIdx.filter(({ f }) => f.pinned || visible.has(f)),
      overflow: withIdx.filter(({ f }) => !f.pinned && !visible.has(f)),
    };
  }, [norm, budget, defaultFieldWidth]);

  const hasOverflow = overflow.length > 0;

  const hasRail = Boolean(modes || actions || status || hasOverflow);

  const rightRail = (
    // flex-shrink: 0 + the outer row's flex-nowrap keep the rail glued to the fields'
    // line — it can never drop to a line of its own, whatever the field widths do.
    <div ref={railRef} className="d-flex flex-nowrap align-items-end gap-2" style={{ flexShrink: 0 }}>
      {modes}
      {actions}
      {hasOverflow && (
        <button
          type="button"
          className={`btn btn-sm btn-outline-primary${showAll ? ' active' : ''}`}
          onClick={() => setShowAll((v) => !v)}
          aria-expanded={showAll}
        >
          <i className="fe fe-sliders me-1" />
          {allFiltersLabel}
          {activeCount != null && activeCount > 0 && (
            <span className="badge bg-primary ms-1">{activeCount}</span>
          )}
        </button>
      )}
      {status}
    </div>
  );

  const body = (
    <>
      {/* w-100 so the row fills its container even when the parent is a flex box
          (e.g. a card-header) — otherwise it shrinks to content and the fit math
          sees far too little width and collapses everything but the pinned field. */}
      <div ref={rowRef} className="d-flex flex-nowrap align-items-end gap-2 w-100">
        {/* Fields live in their own wrapper so any width-estimate slack is absorbed
            here (a field wraps within the wrapper) rather than bumping the rail off
            its line. The wrapper grows to push the rail right only when filters
            overflow; otherwise it hugs its content so the rail sits next to them. */}
        <div
          className="d-flex flex-wrap align-items-end gap-2"
          style={{ flex: hasOverflow ? '1 1 auto' : '0 1 auto', minWidth: 0 }}
        >
          {primary.map(({ f, idx }) => (
            <div style={fieldStyle(f.width, defaultFieldWidth)} key={idx}>
              {f.node}
            </div>
          ))}
        </div>
        {hasRail && rightRail}
      </div>
      {hasOverflow && showAll && (
        <div className="d-flex flex-wrap align-items-end gap-2 pt-2 mt-1 border-top">
          {overflow.map(({ f, idx }) => (
            <div style={fieldStyle(f.width, defaultFieldWidth)} key={idx}>
              {f.node}
            </div>
          ))}
        </div>
      )}
    </>
  );

  if (!card) return className ? <div className={className}>{body}</div> : <>{body}</>;
  return (
    <div className={`card custom-card mb-2${className ? ` ${className}` : ''}`}>
      <div className="card-body py-2 px-2">{body}</div>
    </div>
  );
}
